import type { StatTone } from "../../components/StatValue";

/**
 * Display helpers for the fixture page — one place for the rounding and
 * colouring rules so a number reads the same in every section it appears in.
 *
 * Not a component file on purpose: `react/only-export-components` is an error
 * budget this repo actually enforces (see `toDisplayPercents.ts`'s own note on
 * why it has its own module), and a file mixing JSX with plain functions is
 * what breaks Vite's fast refresh for the whole tree around it.
 */

/** "27.0%" — one decimal, the floor `--size-mono-min` implies for every number
    on this page. */
export function formatPct1(p: number): string {
  return `${(p * 100).toFixed(1)}%`;
}

/** "$3.25", or an em dash for a probability too small to price — same posture
    as `props/fairPrice.ts`. */
export function formatPrice(n: number | null): string {
  if (n === null) return "—";
  return `$${n.toFixed(n >= 10 ? 1 : 2)}`;
}

/** Below this many points, a non-flagged gap is shown as having "no claim"
    rather than picking a side — see `gapDisplay` below. */
const NEGLIGIBLE_GAP_PTS = 1;

export interface GapDisplay {
  tone: StatTone;
  negligible: boolean;
  text: string;
}

/**
 * How one gap (Kev minus the market) is shown.
 *
 * ⚠️ `gap` ARRIVES IN PERCENTAGE POINTS, NOT AS A PROBABILITY — corrected
 * 2026-09-08. `export_fixture_pages.py` computes it as
 * `round((kev - marketP) * 100, 2)`, the same points the exporter's own
 * `FLAG_PTS = 5` threshold and `band` are expressed in. This function used to
 * multiply by another 100, so the biggest real gap in the published file —
 * 10.93 points on an EPL fixture — would have rendered as "+1093.0", in
 * flagged green. Nobody ever saw it: `gapDisplay` is used only by
 * `ResultSection` and `ValueSection`, the two gated sections that had never
 * once parsed. It became reachable the moment the paywall bug was fixed, so
 * it is fixed in the same change.
 *
 * ── THE RULE, FROM THE DESIGN SYSTEM ─────────────────────────────────────────
 * `--edge` green / `--drift` red are used ONLY for a FLAGGED gap — the model
 * saying this one is worth calling out. An unflagged gap is still real
 * arithmetic and still worth printing, but colouring it green or red would
 * assert a call the export did not make, so it stays `--cyan` ("data") like
 * every other number on the page. `--muted-small` is reserved for a gap so
 * small it is not a claim about anything — rounds to under a point AND was not
 * flagged (a flagged gap is never shown as negligible, whatever its size: the
 * exporter chose to flag it).
 */
export function gapDisplay(gap: number | null, flagged: boolean): GapDisplay {
  if (gap === null) return { tone: "neutral", negligible: false, text: "—" };

  const pts = Math.round(gap * 10) / 10; // one decimal point, already in points
  const negligible = !flagged && Math.abs(pts) < NEGLIGIBLE_GAP_PTS;
  const tone: StatTone = flagged ? (pts >= 0 ? "edge" : "drift") : "data";
  const sign = pts > 0 ? "+" : pts < 0 ? "−" : "";
  const text = `${sign}${Math.abs(pts).toFixed(1)}`;
  return { tone, negligible, text };
}

/** A short, locale-aware date — used everywhere this page states when
    something was captured rather than what it says. */
export function formatAsOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

/** Kickoff, spelled out with the weekday — the one date on the page a reader
    is actually going to act on. */
export function formatKickoff(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

/** Kickoff time ALONE, local — for a row that already sits under a day
    heading naming the date, where repeating it would be the same fact
    printed twice on the same screen. */
export function formatKickoffTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(d);
}

/** The local calendar day a kickoff falls on — the landing list's grouping
    key, so a kickoff just either side of midnight in the reader's own
    timezone groups under the day they will actually experience it as,
    never the exporter's UTC date. */
export function localDayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d); // en-CA gives YYYY-MM-DD, which also sorts correctly as a string
}

/** The day heading itself — "Today", "Tomorrow", or the spelled-out date,
    read against the reader's own local midnight rather than the server's. */
export function formatDayHeading(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  const today = new Date();
  const dayKey = localDayKey(iso);
  if (dayKey === localDayKey(today.toISOString())) return "Today";

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dayKey === localDayKey(tomorrow.toISOString())) return "Tomorrow";

  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(d);
}
